import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DirectivesModule } from '@app/directives/directives.module';
import { AngularSvgIconModule } from 'angular-svg-icon';
import {
  BehaviorSubject,
  Observable,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {
  HostelRef,
  PlaceRef,
  PlacesService,
  SearchSuggestion,
} from '../explore/places.service';
import { ExploreCityRef } from '../services/api/api.service';
import { HostelComponent } from './hostel/hostel.component';
import { PlaceComponent } from './place/place.component';

@Component({
  selector: 'app-where',
  templateUrl: './where.component.html',
  styleUrls: ['./where.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    PlaceComponent,
    HostelComponent,
    DirectivesModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: WhereComponent,
    },
  ],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ transform: 'translateY(-8px)', opacity: 0.4 }),
        animate(
          '0.15s ease-in',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
    ]),
  ],
})
export class WhereComponent implements ControlValueAccessor {
  @Input() public active = false;
  public places?: Observable<any>;
  public searching = new BehaviorSubject(false);
  public focused = new BehaviorSubject(false);
  public query!: Observable<string>;

  // todo control valuea accessor on each element
  @ViewChild('whereInput')
  public whereInput?: ElementRef;

  showSearches = new BehaviorSubject(false);
  selection: any;
  focus?: string;

  private observerLeft?: IntersectionObserver;
  private observerRight?: IntersectionObserver;

  public cities: Observable<ExploreCityRef[]>;

  private _value: any;
  get value(): string {
    return this._value;
  }
  set value(val: ExploreCityRef | PlaceRef | HostelRef) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
    this.cdr.markForCheck();
  }

  @Input() placeholderText = $localize`Where are you going?`;

  constructor(
    private placesService: PlacesService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit() {
    this.query = fromEvent(this.whereInput!.nativeElement, 'keyup').pipe(
      map((event: any) => event.target.value)
    );

    this.places = this.query.pipe(
      tap(() => {
        this.showSearches.next(true);
      }),
      debounceTime(250),
      distinctUntilChanged(),
      filter((term) => Boolean(term)),
      tap((query: string) => {
        this.searching.next(true);
        this.showSearches.next(true);
      }),
      switchMap((term) => this.placesService.search(term)),
      map((resp) => {
        this.searching.next(false);
        this.showSearches.next(true);

        // map the resp.cities into PlaceRefs that only have the country and countryId
        // remove any duplicates
        //city: string;
        // cityId: string;
        // stateId: string;
        // countryId: string;
        // country: string;
        // searchType: number;
        const flattenedCountries = resp.countries.map((city) => {
          return {
            countryId: city.countryId,
            country: city.country,
            searchType: 4,
            city: null,
            cityId: null,
            stateId: null,
          };
        });

        // remove any duplicates
        const uniqueCountries = flattenedCountries.filter(
          (country, index, self) =>
            index === self.findIndex((t) => t.countryId === country.countryId)
        );

        const results = [
          ...uniqueCountries,
          ...resp.countries,
          ...resp.cities,
          ...resp.states,
          ...resp.hostels,
        ];

        return results;
      }),
      startWith([]),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  setFocus(focus: boolean) {
    this.focused.next(focus);
    // clear display value if focused
    if (focus) {
      this._value = '';
    }
  }

  public trackByCity(i: any, a: any) {
    return a.city;
  }

  onChange = (value: ExploreCityRef | PlaceRef | HostelRef) => {};
  onTouched = () => {};

  private touched = false;

  public onBlurHandler() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  public doUpdate(city: ExploreCityRef | PlaceRef | HostelRef) {
    this.value = city;
    this.showSearches.next(false);
    this.searching.next(false);
    this.focused.next(false);
  }

  public isHostel(val: SearchSuggestion): boolean {
    return val.searchType === 2;
  }

  public isPlace(val: any): boolean {
    return val.searchType === 1 || val.searchType === 4 || val.searchType === 5;
  }

  get displayValue(): string {
    if (!this._value) {
      return '';
    } else if (this.isHostel(this._value)) {
      return `${this._value.name}`;
    }

    return this._value && this._value.cityName
      ? this._value.cityName
      : this._value && this._value.city
      ? this._value.city
      : this._value.state
      ? `${this._value.state}, ${this._value.country}`
      : this._value.country;
  }

  ngOnDestroy() {
    if (this.observerLeft) {
      this.observerLeft.disconnect();
      this.observerLeft = undefined;
    }

    if (this.observerRight) {
      this.observerRight.disconnect();
      this.observerRight = undefined;
    }
  }
}
